<template>
  <div class="public-shop">
    <div v-if="!shop && !loading" class="container">
      <div class="empty-state mt-5">
        <i class="fas fa-store-slash empty-state-icon"></i>
        <h3>Toko tidak ditemukan</h3>
        <p class="text-muted">URL toko mungkin salah atau toko sudah ditutup.</p>
      </div>
    </div>

    <div v-else-if="loading" class="text-center p-5">
      <div class="spinner-border text-primary"></div>
      <p class="text-muted mt-3">Memuat katalog...</p>
    </div>

    <div v-else>
      <header class="shop-header" :style="{ background: themeGradient }">
        <div class="container">
          <div class="d-flex justify-content-between align-items-center py-3 flex-wrap">
            <div class="d-flex align-items-center mb-2 mb-md-0">
              <img v-if="shop.logo_url" :src="shop.logo_url" alt="logo" class="shop-logo mr-3">
              <div>
                <h2 class="text-white mb-0 shop-title">{{ shop.name }}</h2>
                <small class="text-white shop-desc" v-if="shop.description">{{ shop.description }}</small>
              </div>
            </div>
            <div class="header-actions">
              <router-link :to="shopPath('/member/login')" class="btn-member-login mr-2">
                <i class="fas fa-user-circle"></i>
                <span class="d-none d-md-inline">Login Member</span>
              </router-link>
              <a v-if="shop.owner_phone" :href="'https://wa.me/' + waNumber" target="_blank" class="btn btn-light btn-sm mr-2">
                <i class="fab fa-whatsapp"></i> Chat
              </a>
              <router-link :to="shopPath('/keranjang')" class="btn btn-light btn-sm position-relative">
                <i class="fas fa-shopping-cart"></i>
                <span class="d-none d-md-inline"> Keranjang</span>
                <span v-if="cartCount" class="badge badge-danger cart-badge">{{ cartCount }}</span>
              </router-link>
            </div>
          </div>
        </div>
      </header>

      <div class="container mt-4">
        <div class="row mb-3">
          <div class="col-md-8 mb-2 mb-md-0">
            <div class="input-group">
              <div class="input-group-prepend">
                <span class="input-group-text bg-white"><i class="fas fa-search text-muted"></i></span>
              </div>
              <input v-model="search" type="text" class="form-control" placeholder="Cari produk...">
            </div>
          </div>
          <div class="col-md-4">
            <select v-model="filterCategory" class="form-control">
              <option value="">Semua Kategori</option>
              <option v-for="c in categories" :key="c.id" :value="c.id">{{ c.name }}</option>
            </select>
          </div>
        </div>

        <div class="row">
          <div v-if="donationEnabled && donationSettings" class="col-lg-3 col-md-4 col-6 mb-3">
            <div class="product-card h-100 border-success">
              <router-link :to="shopPath('/donasi')" class="text-decoration-none text-dark">
                <div class="card-img-wrap">
                  <img :src="donationImage(donationSettings.cover_image_url)" :alt="donationSettings.title">
                </div>
                <div class="card-body p-3">
                  <span class="badge badge-success mb-2">Donasi</span>
                  <h6 class="mb-1 product-name">{{ donationSettings.title || 'Donasi' }}</h6>
                  <p v-if="donationSettings.show_progress && donationSettings.target_amount > 0" class="small text-muted mb-2">
                    Terkumpul {{ formatRupiah(donationSettings.collected_amount) }}
                  </p>
                  <span class="btn btn-sm btn-success btn-block">Donasi Sekarang</span>
                </div>
              </router-link>
            </div>
          </div>

          <div v-if="lmsEnabled && lmsCourses.length === 0" class="col-lg-3 col-md-4 col-6 mb-3">
            <div class="product-card h-100 border-primary">
              <router-link :to="shopPath('/kursus')" class="text-decoration-none text-dark">
                <div class="placeholder-cover lms-cover">
                  <i class="fas fa-graduation-cap text-primary"></i>
                </div>
                <div class="card-body p-3">
                  <span class="badge badge-primary mb-2">Kursus</span>
                  <h6 class="mb-2 product-name">Katalog Kursus</h6>
                  <span class="btn btn-sm btn-primary btn-block">Lihat Kursus</span>
                </div>
              </router-link>
            </div>
          </div>

          <div v-for="course in lmsCourses" :key="'course-' + course.id" class="col-lg-3 col-md-4 col-6 mb-3">
            <div class="product-card h-100 border-primary">
              <router-link :to="shopPath('/kursus/' + course.slug)" class="text-decoration-none text-dark">
                <div class="card-img-wrap">
                  <img :src="courseImage(course.cover_image_url)" :alt="course.title">
                </div>
                <div class="card-body p-3">
                  <span class="badge badge-primary mb-2">Kursus</span>
                  <h6 class="mb-1 product-name">{{ course.title }}</h6>
                  <p class="text-primary mb-2 price-tag">
                    <b>{{ course.is_free ? 'GRATIS' : formatRupiah(course.price) }}</b>
                  </p>
                  <span class="btn btn-sm btn-outline-primary btn-block">Lihat Kursus</span>
                </div>
              </router-link>
            </div>
          </div>

          <div v-for="p in filteredProducts" :key="p.id" class="col-lg-3 col-md-4 col-6 mb-3">
            <div class="product-card h-100">
              <router-link :to="shopPath('/produk/' + p.slug)" class="text-decoration-none text-dark">
                <div class="card-img-wrap">
                  <img :src="productImage(p.image_url, p.type)" :alt="p.name">
                </div>
                <div class="card-body p-3">
                  <h6 class="mb-1 product-name">{{ p.name }}</h6>
                  <p class="text-primary mb-2 price-tag"><b>{{ formatRupiah(p.price) }}</b></p>
                  <div class="mb-2">
                    <small v-if="p.type === 'digital'" class="badge badge-info">Digital</small>
                    <small v-if="p.stock !== null && p.stock <= 5 && p.type === 'physical'" class="badge badge-warning ml-1">Stok {{ p.stock }}</small>
                  </div>
                  <span class="btn btn-sm btn-primary btn-block">Lihat Detail</span>
                </div>
              </router-link>
            </div>
          </div>
        </div>

        <div v-if="isCatalogEmpty" class="empty-state mt-2">
          <i class="fas fa-box-open empty-state-icon"></i>
          <h5>Belum ada item di katalog</h5>
          <p class="text-muted mb-0">Toko ini sedang menyiapkan produk. Cek lagi nanti ya!</p>
        </div>
      </div>

      <footer class="text-center text-muted py-4 mt-4">
        <small>Powered by <a href="https://starsender.online" target="_blank">StarSender</a></small>
      </footer>
    </div>
  </div>
</template>

<script>
import publicShopPaths from './public_shop_paths';
import {
  applyShopTheme,
  shopHeaderBackground,
  productImage,
  courseImage,
  donationImage,
  shopPublicMixin,
} from '@/components/shop_public/shop_public_utils';
import { trackPageView, trackSearch, trackViewCategory } from '@/components/shop_public/shop_tracking';
import '@/components/shop_public/shop_public.css';
import '@/components/shop_public/member_portal.css';

export default {
  mixins: [publicShopPaths, shopPublicMixin],
  data() {
    return {
      shop: null,
      products: [],
      categories: [],
      loading: true,
      search: '',
      filterCategory: '',
      donationEnabled: false,
      donationSettings: null,
      lmsEnabled: false,
      lmsCourses: [],
      searchTimer: null,
      lastTrackedSearch: '',
    };
  },
  computed: {
    themeGradient() {
      return shopHeaderBackground(this.shop);
    },
    waNumber() {
      const n = this.shop?.owner_phone || '';
      return n.replace(/^0/, '62').replace(/[^\d]/g, '');
    },
    cartCount() {
      return this.$store.getters['public_shop/cartCount'] || 0;
    },
    filteredProducts() {
      let list = this.products.filter(p => p.status === 'active');
      if (this.search) {
        const s = this.search.toLowerCase();
        list = list.filter(p => p.name.toLowerCase().includes(s));
      }
      if (this.filterCategory) {
        list = list.filter(p => p.category_id == this.filterCategory);
      }
      return list;
    },
    isCatalogEmpty() {
      const hasDonation = this.donationEnabled && this.donationSettings;
      const hasLms = this.lmsEnabled;
      return this.filteredProducts.length === 0 && !hasDonation && !hasLms;
    },
  },
  watch: {
    search(val) {
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => {
        const q = String(val || '').trim();
        if (q.length >= 2 && q !== this.lastTrackedSearch) {
          this.lastTrackedSearch = q;
          trackSearch(q);
        }
      }, 600);
    },
    filterCategory(val) {
      if (!val) return;
      const cat = this.categories.find(c => String(c.id) === String(val));
      if (cat) trackViewCategory(cat.name, cat.id);
    },
  },
  async mounted() {
    this.$store.dispatch('public_shop/cart_load', this.effectiveSlug);
    await this.load();
  },
  methods: {
    productImage,
    courseImage,
    donationImage,
    async load() {
      this.loading = true;
      try {
        const slug = this.effectiveSlug;
        if (!slug && this.onCustomDomain) {
          await this.$store.dispatch('public_shop/public_get_shop_by_domain');
          this.$store.commit('public_shop/SET_CUSTOM_DOMAIN_SLUG', this.$store.state.public_shop.shop.slug);
        }
        const useSlug = this.effectiveSlug;
        const r1 = await this.$store.dispatch('public_shop/public_get_shop', useSlug);
        this.shop = r1.data.data.shop;
        applyShopTheme(this.shop);
        trackPageView({ page_type: 'catalog', content_name: this.shop.name });
        const r2 = await this.$store.dispatch('public_shop/public_get_products', { slug: useSlug, params: {} });
        this.products = r2.data.data.products || [];
        try {
          const r3 = await this.$store.dispatch('public_shop/public_get_categories', useSlug);
          this.categories = r3.data.data.categories || [];
        } catch (e) {
          this.categories = [];
        }
        try {
          const r4 = await this.$store.dispatch('public_shop/public_get_donation_settings', useSlug);
          this.donationSettings = r4.data.data;
          this.donationEnabled = r4.data.data?.is_enabled === true;
        } catch (e) {
          this.donationEnabled = false;
          this.donationSettings = null;
        }
        try {
          await this.$store.dispatch('public_shop/public_get_lms_settings', useSlug);
          this.lmsEnabled = true;
          const r5 = await this.$store.dispatch('public_shop/public_get_lms_courses', useSlug);
          this.lmsCourses = r5.data.data.courses || [];
        } catch (e) {
          this.lmsEnabled = false;
          this.lmsCourses = [];
        }
      } catch (e) {
        this.shop = null;
      } finally {
        this.loading = false;
      }
    },
  },
  metaInfo() {
    return { title: this.shop?.name || 'Toko' };
  },
};
</script>

<style scoped>
.shop-logo {
  height: 48px;
  border-radius: 10px;
  object-fit: cover;
}
.shop-title {
  font-size: 22px;
  font-weight: 600;
}
.shop-desc {
  opacity: 0.9;
}
.cart-badge {
  position: absolute;
  top: -8px;
  right: -8px;
}
.product-name {
  font-size: 14px;
  line-height: 1.35;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.price-tag {
  font-size: 15px;
}
.border-success {
  box-shadow: 0 0 0 1px rgba(40, 167, 69, 0.2);
}
.border-primary {
  box-shadow: 0 0 0 1px rgba(0, 123, 255, 0.15);
}
</style>
